/**
 * Payments.
 *
 * The invalidation here is deliberately narrow. A payment settling changes what one sale has been
 * paid and what one shift is holding — it does not change stock, it does not change the catalogue,
 * and it must not make a till refetch a report nobody is looking at.
 *
 * There is no optimistic update on any of these. A sale can be optimistic because the salesperson
 * is the one making it true; money cannot, because Safaricom is. Showing a payment as settled
 * before the server says so is the one lie this app must never tell.
 */

import { api } from '../api.js';
import {
  cashPositionTag,
  paymentListTag,
  paymentTag,
  salePositionTag,
  saleTag,
  shiftTag,
  unmatchedListTag,
} from '../tags.js';
import type { Paged, SaleId, ShopId } from '../../types/api.js';
import type {
  ConfirmManuallyBody,
  CustomerCredit,
  EnterCodeBody,
  MpesaEnvironment,
  Payment,
  PaymentId,
  PaymentState,
  PaymentVerification,
  Reconciliation,
  RecordPaymentBody,
  ResolveUnmatchedBody,
  SalePaymentPosition,
  StkPushBody,
  UnmatchedId,
  UnmatchedPayment,
} from '../../types/payments.js';

export type PaymentListParams = {
  readonly shopId: ShopId;
  /** A stable label for this list, so one screen's filter never evicts another's. */
  readonly scope: string;
  readonly saleId?: SaleId | undefined;
  readonly method?: string | undefined;
  readonly state?: PaymentState | undefined;
  readonly verification?: PaymentVerification | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly search?: string | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type WithKey<T> = {
  readonly body: T;
  /**
   * Generated before the tap, reused on every retry. A resubmission after a dropped connection
   * returns the original payment rather than taking the money twice.
   */
  readonly idempotencyKey: string;
};

export const paymentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Environment ─────────────────────────────────────────────────────────
    getMpesaEnvironment: build.query<MpesaEnvironment, void>({
      query: () => '/payments/mpesa/environment',
      // Configuration, not data. It changes on a deploy, not during a shift.
      keepUnusedDataFor: 3600,
      providesTags: ['MpesaEnvironment'],
    }),

    // ── Taking money ────────────────────────────────────────────────────────
    recordPayment: build.mutation<Payment, WithKey<RecordPaymentBody>>({
      query: ({ body, idempotencyKey }) => ({
        url: '/payments',
        method: 'POST',
        body,
        idempotencyKey,
      }),
      invalidatesTags: (_result, _error, { body }) => [
        ...(body.saleId ? [salePositionTag(body.saleId), saleTag(body.saleId)] : []),
        paymentListTag(body.shopId, 'recent'),
        // Cash in a pocket changes what the shift is holding, which is the number the
        // salesperson is about to declare.
        ...(body.method === 'cash' ? [cashPositionTag(body.shopId)] : []),
        ...(body.saleId ? [] : [unmatchedListTag(body.shopId)]),
      ],
    }),

    initiateStkPush: build.mutation<Payment, WithKey<StkPushBody>>({
      query: ({ body, idempotencyKey }) => ({
        url: '/payments/mpesa/stk-push',
        method: 'POST',
        body,
        idempotencyKey,
      }),
      // Nothing is invalidated on the push itself: nothing has settled. The waiting screen polls
      // the payment, and settlement invalidates when it actually happens.
      invalidatesTags: (_result, _error, { body }) => [paymentListTag(body.shopId, 'recent')],
    }),

    enterMpesaCode: build.mutation<Payment, WithKey<EnterCodeBody>>({
      query: ({ body, idempotencyKey }) => ({
        url: '/payments/mpesa/enter-code',
        method: 'POST',
        body,
        idempotencyKey,
      }),
      invalidatesTags: (result, _error, { body }) => [
        ...(body.saleId ? [salePositionTag(body.saleId), saleTag(body.saleId)] : []),
        ...(result ? [paymentTag(result.id)] : []),
        paymentListTag(body.shopId, 'recent'),
      ],
    }),

    // ── Reading ─────────────────────────────────────────────────────────────
    getPayment: build.query<Payment, PaymentId>({
      query: (id) => `/payments/${id}`,
      providesTags: (_result, _error, id) => [paymentTag(id)],
    }),

    listPayments: build.query<Paged<Payment>, PaymentListParams>({
      query: ({ scope: _scope, ...params }) => ({
        url: '/payments',
        params: { page: 1, pageSize: 25, ...params },
      }),
      providesTags: (result, _error, { shopId, scope }) => [
        paymentListTag(shopId, scope),
        ...(result?.items ?? []).map((payment) => paymentTag(payment.id)),
      ],
    }),

    getSalePosition: build.query<SalePaymentPosition, SaleId>({
      query: (id) => `/sales/${id}/payment-position`,
      providesTags: (_result, _error, id) => [salePositionTag(id)],
    }),

    getReconciliation: build.query<
      Reconciliation,
      { shopId: ShopId; from?: string | undefined; to?: string | undefined }
    >({
      query: ({ shopId, from, to }) => ({
        url: '/payments/reconciliation',
        params: { shopId, from, to },
      }),
      providesTags: (_result, _error, { shopId }) => [
        paymentListTag(shopId, 'reconciliation'),
        unmatchedListTag(shopId),
      ],
    }),

    // ── Owner-only ──────────────────────────────────────────────────────────
    confirmPaymentManually: build.mutation<
      Payment,
      { paymentId: PaymentId; shopId: ShopId; body: ConfirmManuallyBody }
    >({
      query: ({ paymentId, body }) => ({
        url: `/payments/${paymentId}/confirm-manually`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, _error, { paymentId, shopId }) => [
        paymentTag(paymentId),
        paymentListTag(shopId, 'recent'),
        // Confirmed-versus-provisional is the number this changes, so the reconciliation screen
        // has to hear about it immediately.
        paymentListTag(shopId, 'reconciliation'),
        ...(result?.saleId ? [salePositionTag(result.saleId), saleTag(result.saleId)] : []),
      ],
    }),

    // ── Unmatched ───────────────────────────────────────────────────────────
    listUnmatched: build.query<
      Paged<UnmatchedPayment>,
      { shopId: ShopId; status?: string | undefined; page?: number | undefined; pageSize?: number | undefined }
    >({
      query: ({ shopId, status, page, pageSize }) => ({
        url: '/payments/unmatched',
        params: { shopId, status, page: page ?? 1, pageSize: pageSize ?? 25 },
      }),
      providesTags: (_result, _error, { shopId }) => [unmatchedListTag(shopId)],
    }),

    resolveUnmatched: build.mutation<
      UnmatchedPayment,
      { unmatchedId: UnmatchedId; shopId: ShopId; body: ResolveUnmatchedBody }
    >({
      query: ({ unmatchedId, body }) => ({
        url: `/payments/unmatched/${unmatchedId}/resolve`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, _error, { shopId }) => [
        unmatchedListTag(shopId),
        paymentListTag(shopId, 'reconciliation'),
        ...(result?.attachedSaleId
          ? [salePositionTag(result.attachedSaleId), saleTag(result.attachedSaleId)]
          : []),
      ],
    }),

    listCustomerCredits: build.query<
      Paged<CustomerCredit>,
      { shopId: ShopId; status?: string | undefined; search?: string | undefined }
    >({
      query: (params) => ({ url: '/customer-credit', params }),
      providesTags: (_result, _error, { shopId }) => [paymentListTag(shopId, 'credit')],
    }),

    // ── Development only ────────────────────────────────────────────────────
    /**
     * Replays a callback locally. The endpoint refuses in production, so a build that reaches
     * for it there gets a clean 403 rather than inventing money.
     */
    simulateCallback: build.mutation<unknown, { paymentId: PaymentId; scenario: string }>({
      query: (body) => ({ url: '/payments/mpesa/simulate', method: 'POST', body }),
      invalidatesTags: (_result, _error, { paymentId }) => [paymentTag(paymentId)],
    }),
  }),
});

export const {
  useGetMpesaEnvironmentQuery,
  useRecordPaymentMutation,
  useInitiateStkPushMutation,
  useEnterMpesaCodeMutation,
  useGetPaymentQuery,
  useListPaymentsQuery,
  useGetSalePositionQuery,
  useGetReconciliationQuery,
  useConfirmPaymentManuallyMutation,
  useListUnmatchedQuery,
  useResolveUnmatchedMutation,
  useListCustomerCreditsQuery,
  useSimulateCallbackMutation,
} = paymentsApi;

export { shiftTag };
